import { ArrowUp } from 'lucide-react'

import { cn } from '@/app/utils/cn'
import {
  MOBILE_APP_CONTENT,
  type MobileAppVariant,
} from '@/app/config/mobileAppContent'
import StartLogoVerde from '@/assets/start-logo-verde'
import { Button } from '../ui/button'
import { useMobileAppBannerController } from './useMobileAppBannerController'

interface MobileAppBannerProps {
  variant: MobileAppVariant
  onOpenModal: () => void
}

export function MobileAppBanner({ variant, onOpenModal }: MobileAppBannerProps) {
  const { banner } = MOBILE_APP_CONTENT[variant]
  const {
    isVisible,
    drag,
    isDragging,
    opacity,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleKeyDown,
  } = useMobileAppBannerController({ variant, onOpenModal })

  if (!isVisible) return null

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      style={{
        transform: `translate(${drag.x}px, ${drag.y}px)`,
        opacity,
        transition: isDragging
          ? 'none'
          : 'transform 200ms ease, opacity 200ms ease',
        touchAction: 'none',
      }}
      className={cn(
        'fixed bottom-4 left-4 right-4 z-40 cursor-pointer select-none',
        'md:left-auto md:right-6 md:w-[400px] md:max-w-[calc(100vw-3rem)]',
        'flex items-center justify-between gap-3',
        'rounded-2xl bg-[#60DC9E] px-4 py-3 shadow-lg'
      )}
    >
      <div className="flex items-center gap-3 flex-1">
        <StartLogoVerde
          title="Start Capital"
          className="h-9 w-9 flex-shrink-0"
        />
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-bold text-[#054532]">{banner.title}</h3>
          <p className="text-xs leading-snug text-[#054532]">
            {banner.description}
          </p>
        </div>
      </div>

      <Button
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onOpenModal}
        variant="ghost"
        size="icon"
        className="h-10 w-10 flex-shrink-0 rounded-full bg-[#054532] text-white hover:bg-[#043826] hover:text-white"
      >
        <ArrowUp className="h-5 w-5" />
      </Button>
    </div>
  )
}
